/**
 * The decisions panel: what the room still has to decide and what it has
 * decided, live. An open item is a button that takes the meeting to it; a
 * decision that just landed (from anyone in the room) lights up for a moment.
 */

import { useEffect, useId, useRef, useState, type ReactElement } from 'react'
import { Badge, Caption1, makeStyles, mergeClasses, tokens } from '@fluentui/react-components'
import {
  ArrowRight16Regular,
  CheckmarkCircle16Regular,
  Circle16Regular,
  Flag16Regular,
  Gavel16Regular,
  Lightbulb16Regular,
  Megaphone16Regular,
  People16Regular,
  Person16Regular,
  Target16Regular,
} from '@fluentui/react-icons'
import { decisionLog, type DecisionItem, type DecisionKind } from '../../lib/planning'
import type { Plan, PlanState } from '../../types/planning'

const ICON: Record<DecisionKind, ReactElement> = {
  goal: <Target16Regular />,
  move: <Flag16Regular />,
  owner: <Person16Regular />,
  question: <Gavel16Regular />,
  said: <Megaphone16Regular />,
  request: <People16Regular />,
  idea: <Lightbulb16Regular />,
}

const useStyles = makeStyles({
  rail: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    minWidth: 0,
  },
  group: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  head: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    margin: 0,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    margin: 0,
    padding: 0,
    listStyle: 'none',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '16px 1fr',
    columnGap: tokens.spacingHorizontalS,
    alignItems: 'start',
    width: '100%',
    padding: `6px ${tokens.spacingHorizontalS}`,
    boxSizing: 'border-box',
    borderRadius: tokens.borderRadiusMedium,
    border: 'none',
    background: 'transparent',
    color: tokens.colorNeutralForeground1,
    fontFamily: 'inherit',
    fontSize: tokens.fontSizeBase300,
    textAlign: 'start',
  },
  go: {
    cursor: 'pointer',
    ':hover': { background: tokens.colorNeutralBackground1Hover },
    ':focus-visible': { outline: `2px solid ${tokens.colorStrokeFocus2}` },
  },
  icon: {
    display: 'inline-flex',
    paddingTop: '2px',
    color: tokens.colorNeutralForeground3,
  },
  openIcon: { color: tokens.colorPaletteDarkOrangeForeground1 },
  madeIcon: { color: tokens.colorPaletteGreenForeground1 },
  text: { minWidth: 0, overflowWrap: 'anywhere' },
  note: { display: 'block', color: tokens.colorNeutralForeground3 },
  arrow: { display: 'inline-flex', verticalAlign: 'middle', marginLeft: '4px' },
  // A decision that just came in, from anyone on the page.
  fresh: {
    animationName: {
      from: { backgroundColor: tokens.colorPaletteLightGreenBackground2 },
      to: { backgroundColor: 'transparent' },
    },
    animationDuration: '4s',
    animationTimingFunction: 'ease-out',
    '@media (prefers-reduced-motion: reduce)': {
      animationName: 'none',
      backgroundColor: tokens.colorPaletteLightGreenBackground1,
    },
  },
  none: {
    color: tokens.colorNeutralForeground3,
    paddingLeft: tokens.spacingHorizontalS,
  },
})

/**
 * Keys that were not there on the previous render, for a few seconds. The
 * first render marks nothing: what was decided before the page opened is not
 * news. Compared with the previous render only, so a decision undone and made
 * again lights up again.
 */
function useFresh(keys: string[]): Set<string> {
  const prev = useRef<Set<string> | null>(null)
  const [fresh, setFresh] = useState<Set<string>>(() => new Set())
  // JSON, not a joined string: a goal can span lines.
  const sig = JSON.stringify(keys)
  useEffect(() => {
    const now = new Set(JSON.parse(sig) as string[])
    const was = prev.current
    prev.current = now
    if (!was) return
    const added = [...now].filter((k) => !was.has(k))
    if (added.length) setFresh(new Set(added))
  }, [sig])
  // Its own timer: a later change with nothing new must not cancel it.
  useEffect(() => {
    if (!fresh.size) return
    const t = setTimeout(() => setFresh(new Set()), 4000)
    return () => clearTimeout(t)
  }, [fresh])
  return fresh
}

export function DecisionRail({
  plan,
  state,
  onGo,
}: {
  plan: Plan
  state: PlanState
  /** Bring an open item into view. */
  onGo: (item: DecisionItem) => void
}) {
  const styles = useStyles()
  const { open, made } = decisionLog(plan, state)
  const fresh = useFresh(made.map((d) => d.key))
  const id = useId()

  const body = (d: DecisionItem, done: boolean) => (
    <>
      <span
        className={mergeClasses(styles.icon, done ? styles.madeIcon : styles.openIcon)}
        aria-hidden="true"
      >
        {ICON[d.kind]}
      </span>
      <span className={styles.text}>
        {d.text}
        {!done && (
          <span className={styles.arrow} aria-hidden="true">
            <ArrowRight16Regular />
          </span>
        )}
        {d.note && <Caption1 className={styles.note}>{d.note}</Caption1>}
      </span>
    </>
  )

  return (
    <div className={styles.rail} data-testid="decision-rail">
      <section className={styles.group} aria-labelledby={`${id}-open`}>
        <h2 className={styles.head} id={`${id}-open`}>
          <Circle16Regular aria-hidden="true" />
          To decide
          <Badge appearance="tint" shape="rounded" color={open.length ? 'warning' : 'success'}>
            {open.length}
          </Badge>
        </h2>
        {open.length === 0 ? (
          <Caption1 className={styles.none}>Nothing open.</Caption1>
        ) : (
          <ul className={styles.list}>
            {open.map((d) => (
              <li key={d.key}>
                <button
                  type="button"
                  className={mergeClasses(styles.row, styles.go)}
                  onClick={() => onGo(d)}
                >
                  {body(d, false)}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
      <section className={styles.group} aria-labelledby={`${id}-made`}>
        <h2 className={styles.head} id={`${id}-made`}>
          <CheckmarkCircle16Regular aria-hidden="true" />
          Decided
          <Badge appearance="tint" shape="rounded" color="success">
            {made.length}
          </Badge>
        </h2>
        {made.length === 0 ? (
          <Caption1 className={styles.none}>Nothing yet.</Caption1>
        ) : (
          <ul className={styles.list}>
            {made.map((d) => (
              <li
                key={d.key}
                className={mergeClasses(styles.row, fresh.has(d.key) && styles.fresh)}
              >
                {body(d, true)}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}
